import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpContextToken } from "@angular/common/http";
import { Observable } from "rxjs";
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

const TOKEN_RETRIED = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(private auth: AuthService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isGraphqlRequest(request) || this.isAccessRequest(request)) {
      return next.handle(request);
    }

    return this.auth.getAccessToken().pipe(
      switchMap((token) => this.handleAuthorizedRequest(request, next, token))
    );
  }

  private handleAuthorizedRequest(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    token: string
  ): Observable<HttpEvent<unknown>> {
    const authorizedRequest = request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });

    return next.handle(authorizedRequest).pipe(
      catchError((error) => {
        if (!this.isExpiredTokenError(error) || request.context.get(TOKEN_RETRIED)) {
          throw error;
        }

        return this.auth.refreshAccessToken(token).pipe(
          switchMap((renewedToken) =>
            next.handle(
              request.clone({
                context: request.context.set(TOKEN_RETRIED, true),
                setHeaders: { Authorization: `Bearer ${renewedToken}` },
              })
            )
          )
        );
      })
    );
  }

  private isGraphqlRequest(request: HttpRequest<unknown>): boolean {
    return request.url.split('?')[0] === 'graphql' || request.url.includes('/graphql');
  }

  private isAccessRequest(request: HttpRequest<unknown>): boolean {
    const body = request.body as { operationName?: string } | null;
    return body?.operationName === 'RequestJBagotAccess';
  }

  private isExpiredTokenError(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status === 403 &&
      error.error?.message?.toLowerCase() === 'jwt expired'
    );
  }
}
